import { Button, CircularProgress } from "@mui/material";
import { Card } from "src/components/UI/Card";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { useMemo, useState } from "react";
import { useRunLocalSEOAgent } from "../hooks/local-seo-apis";
import EnableLocalSEOAgentDialog from "./EnableLocalSEOAgentDialog";
import useUpdateUserAgents from "../../hooks/useUpdateUserAgents";
import { useQueryClient } from "@tanstack/react-query";

import useSiteGBP1to1Mapping, { IGBPLocation } from "../../hooks/useSiteGBP1to1Maping";

interface ILSNoDataFoundProps {
    selectedProfile: IGBPLocation;
}

function LSNoDataFound({ selectedProfile }: ILSNoDataFoundProps) {
    // const queryClient = useQueryClient();
    const { user_id: userId, _id: siteId } = useSelector(
        (state: RootState) => state?.siteAudit?.siteAuditDetails
    ) ?? {};

    const siteGBP1to1Configurations = useSiteGBP1to1Mapping({ siteId: siteId })

    // const reduxFilteredLocationsData = useSelector(
    //     (state: RootState) => state.localSeo.reduxFilteredLocationsData
    // ) ?? [];

    // const selectedBusinessName = useSelector(
    //     (state: RootState) => state.localSeo.selectedBusinessName
    // ) ?? '';

    // const selectedProfile = useMemo(() => {
    //     if (!siteGBP1to1Configurations || siteGBP1to1Configurations.isConfigurationError) return null;
    //     return siteGBP1to1Configurations.connectedGBPAccount ?? null;
    // }, [siteGBP1to1Configurations]);

    const { localSEORunMutation, handleLocalSEORun } = useRunLocalSEOAgent();
    const { mutate: updateAgents } = useUpdateUserAgents();
    const [open, setOpen] = useState(false);

    const handleRunAgent = () => {
        setOpen(true);
    };

    const confirmRunAgent = (profileToUse?: any) => {
        const profile = profileToUse || selectedProfile;
        const effectiveLocationId = profile?.name ? profile?.name.split('/')[1] : String(profile?.locationId || '');
        if (userId && profile?.accountId && effectiveLocationId) {
            handleLocalSEORun({
                account_id: String(profile.accountId),
                location_id: effectiveLocationId,
                local_keywords: ['']
            }, {
                siteId, 
                userId
            })
        }
    };

    const isButtonDisabled =
        localSEORunMutation.isPending ||
        !userId ||
        (siteGBP1to1Configurations.GBPLocations.length === 0 && !selectedProfile);

    const isConfiguredToOtherSite = Boolean(
        siteGBP1to1Configurations?.connectedSite &&
        siteGBP1to1Configurations.connectedSite._id !== siteId
    );
    const configuredSiteURL = siteGBP1to1Configurations?.connectedSite?.url;
    const isSiteConfiguredToOtherGbp = Boolean(siteGBP1to1Configurations?.isConfigurationError);
    const configuredGbpName = siteGBP1to1Configurations?.connectedGBPAccount?.title;

    const isBlocked = isConfiguredToOtherSite || isSiteConfiguredToOtherGbp;

    const title = isSiteConfiguredToOtherGbp
        ? "Site is Already Configured to Another Google Business Profile"
        : isConfiguredToOtherSite
        ? "Google Business Profile Already Configured"
        : "No Report Data Found";

    const description = isSiteConfiguredToOtherGbp
        ? (configuredGbpName
            ? `This site is already configured with another Google Business Profile: ${configuredGbpName}`
            : "This site is already configured with another Google Business Profile.")
        : isConfiguredToOtherSite
        ? (configuredSiteURL
            ? `This Google Business Profile is already configured with another site: ${configuredSiteURL}`
            : "This Google Business Profile is already configured with another site.")
        : "It looks like the Local SEO agent hasn't been run or completed for this location yet. Navigate to your SEO dashboard to start analysing your Google Business Profile.";

    return (
        <Card>
            <div className="px-6">
                <p className="text-primary font-semibold text-3xl">Local SEO</p>

                <div className="flex flex-col items-center justify-center gap-6 p-6 pb-0 w-full mt-2.5 mb-2.5">
                    <h3 className="font-medium text-lg text-center text-[#1D2939]">
                        {title}
                    </h3>
                    <p className="font-normal text-sm leading-5 text-center text-[#667085]">
                        {description}
                    </p>
                    {!isBlocked && (
                        <Button
                            variant="contained"
                            disabled={isButtonDisabled}
                            onClick={handleRunAgent}
                            sx={{
                                backgroundColor: 'var(--color-primary, #120242)',
                                color: '#FFFFFF',
                                textTransform: 'none',
                                fontWeight: 400,
                                borderRadius: '4px',
                                fontSize: '14px',
                                py: 1,
                                px: 3.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(18, 2, 66, 0.9)'
                                }
                            }}
                        >
                            {localSEORunMutation.isPending ? (
                                <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
                            ) : (
                                "Run Local SEO Agent"
                            )}
                        </Button>
                    )}
                </div>
            </div>

            <EnableLocalSEOAgentDialog
                GBPLocations={[]}
                open={open}
                onClose={() => setOpen(false)}
                onEnable={confirmRunAgent}
                isLoading={localSEORunMutation.isPending}
                showProfileSelector={false}
                selectedProfile={selectedProfile}
            />
        </Card>
    );
}

export default LSNoDataFound;
